import {useEffect, useState} from 'react'
import {Alert, Button, Form, Spinner} from 'react-bootstrap'
import {MessageDescriptor, useIntl} from 'react-intl'
import {
  DELIVERY_MODE,
  EMAIL_MESSAGES as M,
  EM_DASH,
  ENV,
  PASSWORD_MASK,
  PORT_PLAIN,
  PORT_TLS,
  SMTP_EXAMPLE_HOST,
} from './sharedMessages'

export interface IEmailSetupValues {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: number | null
  emailHostUser?: string | null
  emailHostPassword?: string | null
  emailUseTls?: boolean | null
  defaultFromEmail?: string | null
  publicRequireIdentity?: boolean | null
}

interface IResolvedSources {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: string | null
  emailHostUser?: string | null
  emailUseTls?: string | null
  defaultFromEmail?: string | null
}

interface IResolvedEmailSetup {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: number | null
  emailHostUser?: string | null
  emailUseTls?: boolean | null
  defaultFromEmail?: string | null
  hasEmailHostPassword?: boolean | null
  sources?: IResolvedSources
}

interface IEmailSetupSettings {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: number | null
  emailHostUser?: string | null
  emailUseTls?: boolean | null
  defaultFromEmail?: string | null
  hasEmailHostPassword?: boolean | null
}

interface Props {
  values: IEmailSetupSettings | null | undefined
  resolved?: IResolvedEmailSetup | null
  showInheritance?: boolean
  saving?: boolean
  onSave: (input: IEmailSetupValues) => void | Promise<void>
  // Solo se muestra en el nivel Sistema (SuperAdmin) — si se pasa, se agrega
  // el switch de PUBLIC_REQUIRE_IDENTITY y su valor viaja en el mismo Guardar.
  publicRequireIdentity?: boolean | null
  showPublicRequireIdentity?: boolean
  // Si se pasa, se agrega un input + botón para enviar un email de prueba con
  // la configuración ya guardada (no la del formulario sin guardar).
  onSendTestEmail?: (toEmail: string) => void | Promise<void>
  sendingTestEmail?: boolean
}

export const EmailSetupForm: React.FC<Props> = ({
  values,
  resolved,
  showInheritance = false,
  saving = false,
  onSave,
  publicRequireIdentity,
  showPublicRequireIdentity = false,
  onSendTestEmail,
  sendingTestEmail = false,
}) => {
  const intl = useIntl()
  const [testEmailTo, setTestEmailTo] = useState('')
  const [form, setForm] = useState({
    emailMode: values?.emailMode || '',
    emailHost: values?.emailHost || '',
    emailPort: values?.emailPort != null ? String(values.emailPort) : '',
    emailHostUser: values?.emailHostUser || '',
    emailHostPassword: '',
    emailUseTls: values?.emailUseTls ?? true,
    defaultFromEmail: values?.defaultFromEmail || '',
    publicRequireIdentity: Boolean(publicRequireIdentity),
  })
  const [overriding, setOverriding] = useState(
    Boolean(values?.emailMode || values?.emailHost || values?.emailUseTls !== null)
  )
  // DEFAULT_FROM_EMAIL se puede overridear independientemente del resto del
  // transporte SMTP: en AWS SES, por ejemplo, todas las organizaciones pueden
  // compartir el mismo usuario/credenciales y solo cambiar la dirección "from"
  // (que debe estar verificada en SES) por organización.
  const [overridingFrom, setOverridingFrom] = useState(Boolean(values?.defaultFromEmail))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setForm({
      emailMode: values?.emailMode || '',
      emailHost: values?.emailHost || '',
      emailPort: values?.emailPort != null ? String(values.emailPort) : '',
      emailHostUser: values?.emailHostUser || '',
      emailHostPassword: '',
      emailUseTls: values?.emailUseTls ?? true,
      defaultFromEmail: values?.defaultFromEmail || '',
      publicRequireIdentity: Boolean(publicRequireIdentity),
    })
    setOverriding(Boolean(values?.emailMode || values?.emailHost || values?.emailUseTls !== null))
    setOverridingFrom(Boolean(values?.defaultFromEmail))
  }, [values?.emailMode, values?.emailHost, values?.emailPort, values?.emailHostUser, values?.emailUseTls, values?.defaultFromEmail, publicRequireIdentity])

  const isOverridden = !showInheritance || overriding
  const isOverriddenFrom = !showInheritance || overridingFrom

  const inheritedHint = (label: MessageDescriptor, value: any) => {
    if (!showInheritance || !resolved) return null
    if (value === null || value === undefined || value === '')
      return intl.formatMessage(M.inheritedFromUpper)
    return intl.formatMessage(M.inheritedPair, {
      label: intl.formatMessage(label),
      value: String(value),
    })
  }

  const handleTlsChange = (checked: boolean) => {
    setForm((p) => ({
      ...p,
      emailUseTls: checked,
      emailPort: !p.emailPort || p.emailPort === PORT_TLS || p.emailPort === PORT_PLAIN
        ? (checked ? PORT_TLS : PORT_PLAIN)
        : p.emailPort,
    }))
  }

  const handleSave = () => {
    setError(null)

    if (isOverriddenFrom && !form.defaultFromEmail) {
      setError(intl.formatMessage(M.errDefaultFromRequired, {name: ENV.defaultFrom}))
      return
    }

    const input: IEmailSetupValues = {
      defaultFromEmail: isOverriddenFrom ? form.defaultFromEmail : null,
      ...(showPublicRequireIdentity ? {publicRequireIdentity: form.publicRequireIdentity} : {}),
    }

    if (!isOverridden) {
      onSave({
        emailMode: null,
        emailHost: null,
        emailPort: null,
        emailHostUser: null,
        emailUseTls: null,
        ...input,
      })
      return
    }

    const passwordMissing = !form.emailHostPassword && !values?.hasEmailHostPassword
    if (!form.emailMode || !form.emailHost || !form.emailPort || !form.emailHostUser || passwordMissing) {
      setError(intl.formatMessage(M.errAllSmtpRequired))
      return
    }

    input.emailMode = form.emailMode
    input.emailHost = form.emailHost
    input.emailPort = Number(form.emailPort)
    input.emailHostUser = form.emailHostUser
    input.emailUseTls = form.emailUseTls
    if (form.emailHostPassword) {
      input.emailHostPassword = form.emailHostPassword
    }

    onSave(input)
  }

  return (
    <div>
      {showInheritance && (
        <div className='d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom'>
          <span className='text-muted fs-7'>{intl.formatMessage(M.inheritanceHint)}</span>
          <Form.Check
            type='switch'
            id='override-all'
            label={intl.formatMessage(M.override)}
            checked={overriding}
            onChange={(e) => setOverriding(e.target.checked)}
          />
        </div>
      )}

      {error && (
        <Alert variant='danger' className='py-2'>
          <small>{error}</small>
        </Alert>
      )}

      <Form.Group className='mb-4'>
        <Form.Label className='fw-bold'>{intl.formatMessage(M.deliveryMode)}</Form.Label>
        <Form.Select
          value={form.emailMode}
          onChange={(e) => {
            const mode = e.target.value
            setForm((p) => ({
              ...p,
              emailMode: mode,
              ...(mode === 'gmail'
                ? {
                    emailHost: p.emailHost || SMTP_EXAMPLE_HOST,
                    emailPort: p.emailPort || PORT_TLS,
                    emailUseTls: true,
                  }
                : {}),
            }))
          }}
          disabled={!isOverridden}
        >
          <option value=''>
            {isOverridden
              ? intl.formatMessage(M.selectOption)
              : inheritedHint(M.hintMode, resolved?.emailMode)}
          </option>
          <option value='smtp'>{DELIVERY_MODE.smtp}</option>
          <option value='gmail'>{DELIVERY_MODE.gmail}</option>
        </Form.Select>
      </Form.Group>

      <h6 className='fw-bold mb-3'>{intl.formatMessage(M.smtpServer)}</h6>

      <div className='row align-items-start mb-3'>
        <Form.Group className='mb-3 col-md-5'>
          <Form.Label className='fw-semibold'>{ENV.host}</Form.Label>
          <Form.Control
            value={form.emailHost}
            onChange={(e) => setForm((p) => ({...p, emailHost: e.target.value}))}
            disabled={!isOverridden}
            placeholder={
              !isOverridden
                ? inheritedHint(M.hintHost, resolved?.emailHost) || ''
                : intl.formatMessage(M.egValue, {example: SMTP_EXAMPLE_HOST})
            }
          />
        </Form.Group>
        <Form.Group className='mb-3 col-md-3'>
          <Form.Label className='fw-semibold'>{ENV.port}</Form.Label>
          <Form.Control
            type='number'
            value={form.emailPort}
            onChange={(e) => setForm((p) => ({...p, emailPort: e.target.value}))}
            disabled={!isOverridden}
            placeholder={
              !isOverridden
                ? inheritedHint(M.hintPort, resolved?.emailPort) || ''
                : form.emailUseTls
                ? PORT_TLS
                : PORT_PLAIN
            }
          />
        </Form.Group>
        <Form.Group className='mb-3 col-md-4'>
          <Form.Label className='fw-semibold mb-2'>{ENV.useTls}</Form.Label>
          <div className='d-flex align-items-center' style={{ minHeight: '38px' }}>
            <Form.Check
              type='switch'
              id='emailUseTls'
              label={
                form.emailUseTls
                  ? intl.formatMessage(M.useTls, {port: PORT_TLS})
                  : intl.formatMessage(M.noTls, {port: PORT_PLAIN})
              }
              checked={Boolean(form.emailUseTls)}
              onChange={(e) => handleTlsChange(e.target.checked)}
              disabled={!isOverridden}
            />
          </div>
        </Form.Group>
      </div>

      <div className='row'>
        <Form.Group className='mb-4 col-md-6'>
          <Form.Label className='fw-semibold'>{ENV.hostUser}</Form.Label>
          <Form.Control
            value={form.emailHostUser}
            onChange={(e) => setForm((p) => ({...p, emailHostUser: e.target.value}))}
            disabled={!isOverridden}
            placeholder={
              !isOverridden ? inheritedHint(M.hintUser, resolved?.emailHostUser) || '' : ''
            }
          />
        </Form.Group>
        <Form.Group className='mb-4 col-md-6'>
          <Form.Label className='fw-semibold'>
            {ENV.hostPassword}
            {values?.hasEmailHostPassword && (
              <span className='text-muted fs-8 ms-1'>
                {intl.formatMessage(M.passwordConfigured)}
              </span>
            )}
          </Form.Label>
          <Form.Control
            type='password'
            value={form.emailHostPassword}
            onChange={(e) => setForm((p) => ({...p, emailHostPassword: e.target.value}))}
            disabled={!isOverridden}
            placeholder={
              !isOverridden
                ? resolved?.hasEmailHostPassword
                  ? intl.formatMessage(M.inheritedConfigured)
                  : intl.formatMessage(M.inheritedNotConfigured)
                : PASSWORD_MASK
            }
          />
        </Form.Group>
      </div>

      <Form.Group className='mb-4'>
        <div className='d-flex align-items-center justify-content-between mb-1'>
          <Form.Label className='fw-bold mb-0'>{ENV.defaultFrom}</Form.Label>
          {showInheritance && (
            <Form.Check
              type='switch'
              id='override-from'
              label={intl.formatMessage(M.override)}
              checked={overridingFrom}
              onChange={(e) => setOverridingFrom(e.target.checked)}
            />
          )}
        </div>
        {showInheritance && (
          <span className='text-muted fs-8 d-block mb-2'>
            {intl.formatMessage(M.defaultFromHint)}
          </span>
        )}
        <Form.Control
          value={form.defaultFromEmail}
          onChange={(e) => setForm((p) => ({...p, defaultFromEmail: e.target.value}))}
          disabled={!isOverriddenFrom}
          placeholder={
            !isOverriddenFrom
              ? inheritedHint(M.hintDefaultFrom, resolved?.defaultFromEmail) || ''
              : ''
          }
        />
      </Form.Group>

      {showPublicRequireIdentity && (
        <Form.Group className='mb-4 pt-3 border-top'>
          <Form.Check
            type='switch'
            id='publicRequireIdentity'
            label={`${ENV.publicRequireIdentity}${EM_DASH}${intl.formatMessage(
              M.publicRequireIdentity
            )}`}
            checked={form.publicRequireIdentity}
            onChange={(e) => setForm((p) => ({...p, publicRequireIdentity: e.target.checked}))}
          />
          <span className='text-muted fs-8 d-block mt-1'>
            {intl.formatMessage(M.publicRequireIdentityHint)}
          </span>
        </Form.Group>
      )}

      <div className='d-flex align-items-center justify-content-between pt-2 border-top'>
        {onSendTestEmail ? (
          <div className='d-flex align-items-center gap-2'>
            <Form.Control
              type='email'
              size='sm'
              placeholder={intl.formatMessage(M.testEmailPlaceholder)}
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              style={{width: '220px'}}
            />
            <Button
              variant='light'
              size='sm'
              onClick={() => onSendTestEmail(testEmailTo)}
              disabled={sendingTestEmail || !testEmailTo}
            >
              {sendingTestEmail ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  {intl.formatMessage(M.sending)}
                </>
              ) : (
                intl.formatMessage(M.sendTestEmail)
              )}
            </Button>
          </div>
        ) : (
          <span />
        )}
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              {intl.formatMessage(M.saving)}
            </>
          ) : (
            intl.formatMessage(M.save)
          )}
        </Button>
      </div>
    </div>
  )
}
